import { FC, useContext, useMemo, useState } from 'react'

import { UUID } from '@builddocs/data-management'
import {
  Application,
  getWorkingDraft,
  updateNumbersSheetsDocument,
  WorkingDraft,
  WorkingDraftPage,
  useSheets,
} from '@builddocs/filestore-api'
import { FILE_STORE_BASE } from '@builddocs/filestore-api'
import { ViewerContext } from '@builddocs/shared/feature-pdf-viewer'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import RotateLeftOutlinedIcon from '@mui/icons-material/RotateLeftOutlined'
import {
  Input,
  TextField,
  Typography,
  Button,
  Stack,
  Checkbox,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material'
import { toast } from 'react-toastify'

interface OwnProps {
  doc: WorkingDraft
  onCheck: (doc: WorkingDraftPage) => void
  selected: Record<UUID, WorkingDraft>
  id?: string
  onUpdateDoc?: (docs: WorkingDraft[]) => void
}

type Props = OwnProps

const ListChecker: FC<Props> = props => {
  const { onCheck, selected, doc, id, onUpdateDoc } = props
  const { sheets, refreshSheets } = useSheets(doc.id)
  const viewerContext = useContext(ViewerContext)
  const [document, setDocument] = useState<WorkingDraft>(doc)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [onChange, setOnChange] = useState(false)

  const initialData = useMemo(() => sheets, [sheets])

  const handleChange = () => {
    setOnChange(prev => !prev)
  }
  const selectedSheets = useMemo(
    () =>
      (Array.isArray(initialData) &&
        initialData
          .filter(list => !!selected[list.id])
          .map(list => list.sheet)
          .sort()) ??
      [],
    [document, selected, initialData],
  )
  const initialNumbersSheets = useMemo(
    () =>
      document.pageCorrespondence || {
        [document.pageNumber || 0]: document.sheet || '',
      },
    [document],
  )
  const [numbersSheets, setNumbersSheets] =
    useState<Record<number, string>>(initialNumbersSheets)

  const refreshDoc = () => {
    setIsLoading(true)
    getWorkingDraft(doc.id)
      .then(res => {
        setDocument(res)
        setIsLoading(false)
      })
      .catch(e => {
        setIsLoading(false)
        toast.error(e?.response?.data?.message ?? 'Неизвестная ошибка')
      })
  }

  const handleChangeNumberSheet = (e, page) => {
    setNumbersSheets(prev => ({ ...prev, [page]: e.target.value }))
  }
  const handleUpdateNumbersSheets = () => {
    updateNumbersSheetsDocument({
      documentType: Application.WORKING_DRAFT,
      documentId: document.id,
      data: numbersSheets,
    })
      .then(response => {
        if (response.status === 200) {
          toast.success('Номера листов обновлены')
          if (response?.data) {
            setDocument(response.data)
            onUpdateDoc &&
              onUpdateDoc([response.data, ...response.data.pagesObjects])
          }
          refreshSheets()
          setIsOpen(false)
        }
      })
      .catch(e => {
        toast.error(e?.response?.data?.message ?? 'Неизвестная ошибка')
      })
  }
  const handleOpenMenu = () => {
    setIsOpen(true)
  }
  const handleCloseMenu = () => {
    setIsOpen(false)
    setNumbersSheets(initialNumbersSheets)
  }

  if (document.pageCorrespondence === null) {
    return (
      <Stack alignItems="center" direction="row" spacing={1}>
        <Typography>Распознание листов, подождите</Typography>
        {isLoading ? (
          <CircularProgress size={28} />
        ) : (
          <IconButton id="reload_docs_button" onClick={refreshDoc}>
            <RotateLeftOutlinedIcon />
          </IconButton>
        )}
      </Stack>
    )
  }
  if (!document.pages?.length) {
    return (
      <>
        {isOpen ? (
          <Stack alignItems="center" direction="row" spacing={1}>
            <TextField
              label="Лист"
              size="small"
              value={numbersSheets[document.pageNumber || 0]}
              onChange={e =>
                handleChangeNumberSheet(e, document.pageNumber || 0)
              }
            />
            <Stack alignItems="center" direction="row" spacing={1}>
              <IconButton
                size="small"
                onClick={() => {
                  handleCloseMenu()
                }}
              >
                <ClearRoundedIcon />
              </IconButton>
              <IconButton
                disabled={
                  numbersSheets[document.pageNumber || 0] ===
                  initialNumbersSheets[document.pageNumber || 0]
                }
                size="small"
                onClick={() => handleUpdateNumbersSheets()}
              >
                <CheckRoundedIcon />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography>
              {`Лист:  ${
                document.sheet ||
                numbersSheets[document.pageNumber || 0] ||
                'не указан'
              }`}
            </Typography>
            <Button
              id={`${id}-button-update`}
              size="small"
              onClick={() => handleOpenMenu()}
            >
              Редактировать
            </Button>
          </Stack>
        )}
      </>
    )
  }
  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between">
        <Button size="small" onClick={() => handleChange()}>
          {selectedSheets &&
            (selectedSheets.length === 1
              ? `Выбран лист: `
              : selectedSheets.length > 1
              ? `Выбраны листы: `
              : `Выбрать листы `)}
          {selectedSheets && selectedSheets.join(', ')}
        </Button>
        {onChange && (
          <Button size="small" onClick={() => handleChange()}>
            Скрыть
          </Button>
        )}
        {isOpen && onChange ? (
          <Stack alignItems="center" direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() => {
                handleCloseMenu()
              }}
            >
              <ClearRoundedIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleUpdateNumbersSheets()}
            >
              <CheckRoundedIcon />
            </IconButton>
          </Stack>
        ) : onChange ? (
          <Button
            id={`${id}-button-update`}
            size="small"
            onClick={() => handleOpenMenu()}
          >
            Редактировать
          </Button>
        ) : null}
      </Stack>
      <Box>
        <Collapse in={onChange}>
          <Box>
            <TableContainer sx={{ maxHeight: 200 }}>
              <Table
                stickyHeader
                aria-label="sticky table"
                aria-labelledby="tableTitle"
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell padding="checkbox">Страница</TableCell>
                    <TableCell padding="checkbox">Лист</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sheets?.map(item => (
                    <TableRow key={item.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!(selected && selected[item.id])}
                          onChange={() => onCheck(item)}
                        />
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Typography>
                          {(item.pageNumber + 1).toString()}
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        {isOpen ? (
                          <Input
                            value={
                              (numbersSheets[item.pageNumber] || '') as string
                            }
                            onChange={e =>
                              handleChangeNumberSheet(e, item.pageNumber)
                            }
                          />
                        ) : (
                          <Typography>
                            {numbersSheets[item.pageNumber] || ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton
                          id={'show_sheet_' + doc.id}
                          onClick={() =>
                            viewerContext.setPDF({
                              url: `${FILE_STORE_BASE}${doc.documentUrl}`,
                              fileName: doc.fileName,
                              fileId: doc.id,
                            })
                          }
                        >
                          <PictureAsPdfOutlinedIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </Box>
    </Stack>
  )
}

export default ListChecker
